import { Circle } from '@mui/icons-material';
import { Button, Skeleton, Stack, Typography } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import { EmptyList } from '@tix-ticketing/ui-components';
import { TicketsAlertIcon } from '@tix-ticketing/ui-icons/feature';
import { endOfDay } from 'date-fns';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { getApiEventGroupsByEventGroupIdEventsPaged } from 'api/generated';
import type { DayOfWeek, SaleStatus, PagedEventResponse, SpecialOfferResponse } from 'api/generated';
import type { CalendarRange } from 'components/events-list/calendar-multi';
import { EventDateCard } from 'components/events-list/event-date-card';
import { useAppState } from 'lib/app-state';
import { getServerWeekday } from 'lib/helpers/date-helpers';
import { CapitalizeFirstLetter } from 'lib/helpers/string-helpers';
import { useServerTimezone } from 'lib/hooks/use-server-timezone';
import { EventsPaginatedFilters } from './events-paginated-filters';
const pageSize = 20;
export default function EventsPaginatedContainer({
  eventGroupId,
  showClearFilters = true,
  specialOffersOnly,
  setSpecialOffersOnly,
  appliedSpecialOffer,
  setAvailableSpecialOffers
}: {
  eventGroupId: number;
  showClearFilters?: boolean;
  specialOffersOnly: boolean;
  setSpecialOffersOnly: (value: boolean) => void;
  appliedSpecialOffer: {
    specialOfferId: number;
    specialOfferPassword?: string;
  } | null;
  setAvailableSpecialOffers: (value: SpecialOfferResponse[]) => void;
}) {
  const appState = useAppState();
  const {
    formatDate,
    timezone
  } = useServerTimezone();
  const [dateRange, setDateRange] = useState<CalendarRange>([null, null]);
  const [from, to] = dateRange;
  const [ticketCount, setTicketCount] = useState<number | null>(null);
  const [days, setDays] = useState<string | null>(null);
  const [availability, setAvailability] = useState<string | null>(null);
  const clearFilters = useCallback(() => {
    setDateRange([null, null]); // Date range
    setTicketCount(null); // Tickets
    setAvailability(null); // Availability
    setDays(null); // Days
    setSpecialOffersOnly(false); // Special offers only
  }, [setSpecialOffersOnly]);
  const daysFilterOptions = useMemo(() => [...[...Array(7).keys()].map(day => ({
    value: new Date(Date.UTC(2021, 5, day)).toLocaleDateString('en', {
      weekday: 'long'
    }).toString(),
    label: CapitalizeFirstLetter(formatDate(new Date(Date.UTC(2021, 5, day)), {
      weekday: 'long'
    }))
  })), {
    value: null,
    label: <FormattedMessage id="event.showAll" defaultMessage={[{
      "type": 0,
      "value": "Show all"
    }]} />
  }], [formatDate]);
  const availabilityFilterOptions = useMemo(() => [{
    value: null,
    label: <FormattedMessage id="event.showAll" defaultMessage={[{
      "type": 0,
      "value": "Show all"
    }]} />,
    icon: null
  }, {
    value: 'Available',
    label: <FormattedMessage id="common.available" defaultMessage={[{
      "type": 0,
      "value": "Available"
    }]} />,
    icon: <Circle sx={{
      height: '10px',
      color: 'success.main'
    }} />
  }, {
    value: 'FewLeft',
    label: <FormattedMessage id="common.fewLeft" defaultMessage={[{
      "type": 0,
      "value": "Few left"
    }]} />,
    icon: <Circle sx={{
      height: '10px',
      color: 'warning.main'
    }} />
  }], []);

  // Data fetching with infinite scroll
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading
  } = useInfiniteQuery<PagedEventResponse, Error>({
    queryKey: ['eventGroupEvents', eventGroupId, pageSize, from, to, ticketCount, availability, days, appliedSpecialOffer, specialOffersOnly, !!appState.tokens],
    queryFn: async context => {
      const pageParam = typeof context.pageParam === 'number' ? context.pageParam : 0;
      const res = await getApiEventGroupsByEventGroupIdEventsPaged({
        path: {
          eventGroupId
        },
        query: {
          PageIndex: pageParam,
          PageSize: pageSize,
          DateFrom: from ? from.toISOString() : undefined,
          // A single-day selection leaves `to` null; treat it as the same
          // day so the range covers just that date rather than everything
          // from the start date onward.
          DateTo: from ? endOfDay(to ?? from).toISOString() : undefined,
          TicketCount: ticketCount ?? undefined,
          Availability: availability ? availability as SaleStatus : undefined,
          Weekday: days !== null ? daysFilterOptions?.find(i => i.value === days)?.value as DayOfWeek | undefined : undefined,
          SpecialOfferId: specialOffersOnly ? appliedSpecialOffer?.specialOfferId ?? undefined : undefined,
          SpecialOfferPassword: specialOffersOnly ? appliedSpecialOffer?.specialOfferPassword ?? undefined : undefined
        }
      });
      return res.data as PagedEventResponse;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage: PagedEventResponse) => {
      if (lastPage.pagination && lastPage.pagination.hasNext && typeof lastPage.pagination.pageIndex === 'number') {
        return lastPage.pagination.pageIndex + 1;
      }
      return undefined;
    },
    enabled: !!eventGroupId
  });

  // Scroll observer
  const loaderRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!loaderRef.current) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }, {
      rootMargin: '300px'
    } // trigger a bit before hitting the bottom
    );
    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Get the special offer header if filtering by special offers
  const specialOfferHeader = useMemo(() => {
    if (specialOffersOnly && appliedSpecialOffer && data?.pages) {
      const specialOffer = data.pages.flatMap(p => p.specialOffers || []).find(s => s?.specialOfferId === appliedSpecialOffer.specialOfferId);
      return specialOffer?.name;
    }
    return null;
  }, [appliedSpecialOffer, data, specialOffersOnly]);
  const availableSpecialOffers = useMemo(() => {
    if (!data?.pages) return [];
    const offers = data.pages.flatMap(p => p.specialOffers || []);
    const uniqueOffers = new Map();
    offers.forEach(o => {
      if (o.specialOfferId && !uniqueOffers.has(o.specialOfferId) && o.eventGroup?.eventGroupId === eventGroupId && o.specialOfferId !== appliedSpecialOffer?.specialOfferId) {
        uniqueOffers.set(o.specialOfferId, o);
      }
    });
    return Array.from(uniqueOffers.values()) as SpecialOfferResponse[];
  }, [data?.pages, eventGroupId, appliedSpecialOffer?.specialOfferId]);
  useEffect(() => {
    setAvailableSpecialOffers(availableSpecialOffers);
  }, [availableSpecialOffers, setAvailableSpecialOffers]);

  // Distinct weekdays and sale statuses present in the loaded events. Filters
  // only offer values that actually appear in the list, and a filter with
  // nothing meaningful to choose between is hidden entirely.
  const loadedEvents = useMemo(() => data?.pages.flatMap(page => (page as PagedEventResponse).events ?? []) ?? [], [data]);
  const availableWeekdays = useMemo(() => {
    const weekdays = new Set<string>();
    loadedEvents.forEach(event => {
      if (event.startDate) {
        weekdays.add(getServerWeekday(event.startDate, timezone));
      }
    });
    return weekdays;
  }, [loadedEvents, timezone]);

  // Earliest start date among loaded events, used to open the date-range
  // picker on the month of the first event rather than the current month.
  const earliestEventDate = useMemo(() => {
    let earliest: Date | null = null;
    loadedEvents.forEach(event => {
      if (event.startDate) {
        const date = new Date(event.startDate);
        if (!earliest || date < earliest) {
          earliest = date;
        }
      }
    });
    return earliest;
  }, [loadedEvents]);
  const availableStatuses = useMemo(() => {
    const statuses = new Set<SaleStatus>();
    loadedEvents.forEach(event => {
      if (event.saleStatus) {
        statuses.add(event.saleStatus);
      }
    });
    return statuses;
  }, [loadedEvents]);

  // Keep the "Show all" option and the currently selected value so the active
  // filter can always be read back and cleared, then add only present values.
  const visibleDaysFilterOptions = useMemo(() => daysFilterOptions.filter(option => option.value === null || option.value === days || availableWeekdays.has(option.value)), [daysFilterOptions, availableWeekdays, days]);
  const visibleAvailabilityFilterOptions = useMemo(() => availabilityFilterOptions.filter(option => option.value === null || option.value === availability || availableStatuses.has(option.value as SaleStatus)), [availabilityFilterOptions, availableStatuses, availability]);
  const showDayFilter = days !== null || availableWeekdays.size > 1;

  // Show the availability filter only when the shows actually differ in
  // availability and at least one of those statuses is selectable, so picking
  // it would narrow the list. When every show shares one status, it's hidden.
  const hasSelectableStatus = availabilityFilterOptions.some(option => option.value !== null && availableStatuses.has(option.value as SaleStatus));
  const showAvailabilityFilter = availability !== null || availableStatuses.size > 1 && hasSelectableStatus;

  // The date-range filter is only useful once the list spans more than one
  // page. It also stays visible while a range is applied so it can be cleared,
  // even if that range narrowed the results down to a single page.
  const totalPages = (data?.pages[0] as PagedEventResponse | undefined)?.pagination?.totalPages ?? 0;
  const showDateRangeFilter = dateRange[0] !== null || totalPages > 1;

  // Handle empty state
  if (!isLoading && (data?.pages[0] as PagedEventResponse)?.events?.length === 0) {
    return <>
        <EventsPaginatedFilters dateRange={dateRange} setDateRange={setDateRange} showDateRangeFilter={showDateRangeFilter} earliestEventDate={earliestEventDate} ticketCount={ticketCount} setTicketCount={setTicketCount} days={days} setDays={setDays} daysFilterOptions={visibleDaysFilterOptions} showDayFilter={showDayFilter} availability={availability} setAvailability={setAvailability} availabilityFilterOptions={visibleAvailabilityFilterOptions} showAvailabilityFilter={showAvailabilityFilter} specialOffersOnly={specialOffersOnly} setSpecialOffersOnly={setSpecialOffersOnly} hasSpecialOffer={appliedSpecialOffer !== null} clearFilters={showClearFilters ? clearFilters : undefined} />
        <EmptyList title={<FormattedMessage id="event.noShows.title" defaultMessage={[{
        "type": 0,
        "value": "No tickets available"
      }]} />} description={<FormattedMessage id="event.noShows.description" defaultMessage={[{
        "type": 0,
        "value": "No tickets available for your selection. Try adjusting or clearing filters."
      }]} />} icon={<TicketsAlertIcon />}
      // The filter bar's own "Clear filters" button is hidden when the
      // List/Calendar toggle takes its place (showClearFilters is false),
      // so surface the action here instead to keep it reachable.
      action={!showClearFilters ? <Button variant="outlined" color="monochrome" onClick={() => clearFilters()}>
                <FormattedMessage id="event.filter.clear" defaultMessage={[{
          "type": 0,
          "value": "Clear filters"
        }]} />
              </Button> : undefined} />
      </>;
  }
  return <>
      <EventsPaginatedFilters dateRange={dateRange} setDateRange={setDateRange} showDateRangeFilter={showDateRangeFilter} earliestEventDate={earliestEventDate} ticketCount={ticketCount} setTicketCount={setTicketCount} days={days} setDays={setDays} daysFilterOptions={visibleDaysFilterOptions} showDayFilter={showDayFilter} availability={availability} setAvailability={setAvailability} availabilityFilterOptions={visibleAvailabilityFilterOptions} showAvailabilityFilter={showAvailabilityFilter} specialOffersOnly={specialOffersOnly} setSpecialOffersOnly={setSpecialOffersOnly} hasSpecialOffer={appliedSpecialOffer !== null} clearFilters={showClearFilters ? clearFilters : undefined} />
      {specialOfferHeader && <Typography variant="h5" align="center" sx={{
      mb: 1
    }}>
          {specialOfferHeader}
        </Typography>}
      <Stack spacing={1.5} sx={{
      pt: {
        sm: 2
      }
    }}>
        {data?.pages.map((page, pageIndex) => <Fragment key={pageIndex}>
            {(page as PagedEventResponse).events?.map(event => <EventDateCard key={event.id} event={event} href={`/${eventGroupId}/event/${event.id}`} />)}
          </Fragment>)}
        {(isLoading || isFetchingNextPage) && [...Array(pageSize)].map((_, i) => <Skeleton key={i} variant="container" height={128} />)}
      </Stack>
      {/* Invisible loader div triggers fetchNextPage when in view */}
      <div ref={loaderRef} />
    </>;
}